import { useState } from 'react'
import {
  makeStyles,
  tokens,
  Title2,
  Body1,
  Button,
  Card,
  Badge,
  Caption1,
} from '@fluentui/react-components'
import {
  ArrowLeft24Regular,
  Add24Regular,
  Folder24Regular,
} from '@fluentui/react-icons'
import { useNavigate } from 'react-router-dom'
import { useCaseStore, CaseType, CaseStatus, CasePriority } from '../stores/caseStore'
import { CreateCaseDialog } from '../components/CreateCaseDialog'

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  content: {
    padding: tokens.spacingVerticalXXL,
    maxWidth: '1200px',
    margin: '0 auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: tokens.spacingVerticalL,
  },
  card: {
    padding: tokens.spacingVerticalL,
    cursor: 'pointer',
    transition: 'box-shadow 0.2s',
    ':hover': {
      boxShadow: tokens.shadow8,
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacingVerticalM,
  },
  cardTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  cardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  badges: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  meta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
    textAlign: 'center',
    gap: tokens.spacingVerticalL,
  },
  emptyIcon: {
    fontSize: '64px',
    color: tokens.colorNeutralForeground3,
  },
})

const getCaseTypeBadgeColor = (type: CaseType): 'informative' | 'warning' | 'danger' | 'success' => {
  switch (type) {
    case 'investigation':
      return 'informative'
    case 'surveillance':
      return 'warning'
    case 'incident':
      return 'danger'
    default:
      return 'success'
  }
}

const getCaseStatusBadgeColor = (status: CaseStatus): 'success' | 'subtle' | 'informative' => {
  switch (status) {
    case 'active':
      return 'success'
    case 'closed':
      return 'subtle'
    default:
      return 'informative'
  }
}

const getCasePriorityBadgeColor = (priority: CasePriority): 'subtle' | 'informative' | 'warning' | 'danger' => {
  switch (priority) {
    case 'low':
      return 'subtle'
    case 'medium':
      return 'informative'
    case 'high':
      return 'warning'
    case 'critical':
      return 'danger'
  }
}

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function Cases() {
  const styles = useStyles()
  const navigate = useNavigate()
  const cases = useCaseStore((s) => s.cases)
  const selectCase = useCaseStore((s) => s.selectCase)
  
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleCaseClick = (caseId: string) => {
    selectCase(caseId)
    navigate(`/cases/${caseId}`)
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            icon={<ArrowLeft24Regular />}
            appearance="subtle"
            onClick={() => navigate('/')}
            aria-label="Back to dashboard"
          />
          <Title2>Cases</Title2>
        </div>
        <Button
          icon={<Add24Regular />}
          appearance="primary"
          onClick={() => setDialogOpen(true)}
        >
          New Case
        </Button>
      </header>

      <main className={styles.content}>
        {cases.length === 0 ? (
          <div className={styles.emptyState}>
            <Folder24Regular className={styles.emptyIcon} />
            <div>
              <Title2>No cases yet</Title2>
              <Body1>Create your first case to get started</Body1>
            </div>
            <Button
              appearance="primary"
              onClick={() => setDialogOpen(true)}
            >
              Create First Case
            </Button>
          </div>
        ) : (
          <div className={styles.grid}>
            {cases.map((caseItem) => (
              <Card
                key={caseItem.id}
                className={styles.card}
                onClick={() => handleCaseClick(caseItem.id)}
              >
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>
                    <Caption1>{caseItem.caseNumber}</Caption1>
                    <Title2>{caseItem.name}</Title2>
                  </div>
                </div>
                
                <div className={styles.cardInfo}>
                  <div className={styles.badges}>
                    <Badge
                      appearance="filled"
                      color={getCaseTypeBadgeColor(caseItem.type)}
                    >
                      {caseItem.type}
                    </Badge>
                    <Badge
                      appearance="filled"
                      color={getCaseStatusBadgeColor(caseItem.status)}
                    >
                      {caseItem.status}
                    </Badge>
                    <Badge
                      appearance="filled"
                      color={getCasePriorityBadgeColor(caseItem.priority)}
                    >
                      {caseItem.priority}
                    </Badge>
                  </div>
                  
                  {caseItem.description && (
                    <Body1>{caseItem.description}</Body1>
                  )}
                  
                  <div className={styles.meta}>
                    <Caption1>{caseItem.videoCount} videos</Caption1>
                    <Caption1>{formatDate(caseItem.createdAt)}</Caption1>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <CreateCaseDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  )
}
